import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { WebService } from '../shared/services/web.service';
import '../../../quill.config';

type DeliveryType = 'instant' | 'scheduled';
type DeepLinkType = 0 | 1 | 2 | 3 | 4;
type ChannelType = 1 | 2; // 1=Push, 2=Announcement
type ListFilter = 'all' | 'push' | 'announcement' | '1' | '2';

interface NotificationItem {
  notification_id: number;
  title: string;
  body: string;
  channel: ChannelType;
  is_active: number;
  delivery_type: 1 | 2;
  scheduled_at: string | null;
  link_type: Exclude<DeepLinkType, 0> | null;
  link_id: number | null;
  link_label: string | null;
  created: string;
}

interface DeepLinkOption {
  id: number;
  label: string;
}

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.css']
})
export class NotificationComponent implements OnInit {
  notificationForm: FormGroup;
  announcementForm: FormGroup;
  notifications: NotificationItem[] = [];
  notificationFormVisible = false;
  updateMode = false;
  selectedNotificationId: number | null = null;
  selectedChannel: ChannelType = 1;
  selectedStatusFilter: ListFilter = 'all';
  isLoading = false;
  showLoad = false;
  isProcessingScheduled = false;
  announcementActive = true;
  linkItems: DeepLinkOption[] = [];
  linkItemsLoading = false;

  scheduleTimeOptions = {
    nzFormat: 'h:mm a',
    nzMinuteStep: 5,
    nzUse12Hours: true,
    nzHideDisabledOptions: true
  };

  announcementModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['code-block'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['clean'],
      ['link']
    ]
  };

  constructor(
    private fb: FormBuilder,
    private notification: NzNotificationService,
    private webapi: WebService
  ) {
    this.notificationForm = this.fb.group({
      title: [null, [Validators.required, Validators.maxLength(120)]],
      body: [null, [Validators.required, Validators.maxLength(1000)]],
      deliveryType: ['instant', [Validators.required]],
      scheduledAt: [null],
      deepLinkType: [0, [Validators.required]],
      deepLinkId: [null]
    });

    this.announcementForm = this.fb.group({
      title: [null, [Validators.required, Validators.maxLength(120)]],
      description: [null, [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.onDeliveryTypeChange();
    this.notificationForm.get('deliveryType')?.valueChanges.subscribe(() => {
      this.onDeliveryTypeChange();
    });
    this.notificationForm.get('deepLinkType')?.valueChanges.subscribe((value) => {
      this.onDeepLinkTypeChange(value as DeepLinkType | null | undefined);
    });
    this.getNotifications();
  }

  get isScheduled(): boolean {
    return this.notificationForm.get('deliveryType')?.value === 'scheduled';
  }

  get isAnnouncementChannel(): boolean {
    return this.selectedChannel === 2;
  }

  get formTitle(): string {
    if (this.updateMode) {
      return this.isAnnouncementChannel ? 'Update Announcement' : 'Update Push Notification';
    }
    return 'Add Notification';
  }

  onFormTabChange(index: number): void {
    if (this.updateMode) {
      return;
    }
    this.selectedChannel = index === 1 ? 2 : 1;
  }

  onDeliveryTypeChange(): void {
    const scheduleControl = this.notificationForm.get('scheduledAt');
    if (!scheduleControl) {
      return;
    }

    if (this.isScheduled) {
      scheduleControl.setValidators([Validators.required]);
    } else {
      scheduleControl.clearValidators();
      scheduleControl.setValue(null);
    }
    scheduleControl.updateValueAndValidity();
  }

  onDeepLinkTypeChange(value: DeepLinkType | null | undefined): void {
    const deepLinkIdControl = this.notificationForm.get('deepLinkId');
    if (!deepLinkIdControl) {
      return;
    }

    const normalizedType = Number(value);
    if (normalizedType !== 1 && normalizedType !== 2 && normalizedType !== 3) {
      this.linkItems = [];
      deepLinkIdControl.clearValidators();
      deepLinkIdControl.setValue(null);
      deepLinkIdControl.updateValueAndValidity();
      return;
    }

    deepLinkIdControl.setValidators([Validators.required]);
    deepLinkIdControl.setValue(null);
    deepLinkIdControl.updateValueAndValidity();
    this.getDeepLinkItems(normalizedType as Exclude<DeepLinkType, 0>);
  }

  getDeepLinkItems(type: Exclude<DeepLinkType, 0>): void {
    this.linkItemsLoading = true;
    this.webapi.requestJson('getNotificationLinkOptions', { link_type: type, limit: 10 }).subscribe(
      (data) => {
        const response = JSON.parse(data.body);
        const options = Array.isArray(response.data) ? response.data : [];
        this.linkItems = options.map((item) => ({
          id: Number(item.id),
          label: item.label || `Item #${item.id}`
        }));
        this.linkItemsLoading = false;
      },
      () => {
        this.linkItemsLoading = false;
        this.linkItems = [];
        this.createToast('error', '', 'Unable to load items for deep link.');
      }
    );
  }

  getNotifications(): void {
    const filters: Array<{ key: string; values: string[] }> = [];
    if (this.selectedStatusFilter === 'push') {
      filters.push({ key: 'channel', values: ['1'] });
    } else if (this.selectedStatusFilter === 'announcement') {
      filters.push({ key: 'channel', values: ['2'] });
    } else if (this.selectedStatusFilter === '1' || this.selectedStatusFilter === '2') {
      filters.push({ key: 'delivery_type', values: [this.selectedStatusFilter] });
      filters.push({ key: 'channel', values: ['1'] });
    }

    this.showLoad = true;
    this.webapi.requestJson('getNotification', {
      Filters: filters.map((filter) => ({
        Key: filter.key,
        Values: filter.values
      }))
    }).subscribe(
      (data) => {
        const response = JSON.parse(data.body);
        this.notifications = (response.data || []).map((item: NotificationItem) => ({
          ...item,
          channel: Number(item.channel) === 2 ? 2 : 1,
          is_active: item.is_active === 0 ? 0 : 1
        }));
        this.showLoad = false;
      },
      () => {
        this.showLoad = false;
        this.createToast('error', '', 'An error occurred. Please try again later.');
      }
    );
  }

  openAddForm(): void {
    this.resetForms();
    this.selectedChannel = 1;
    this.notificationFormVisible = true;
  }

  canEditNotification(item: NotificationItem): boolean {
    // Announcements can always be edited.
    if (Number(item.channel) === 2) {
      return true;
    }
    // Instant push cannot be edited after send.
    if (Number(item.delivery_type) !== 2) {
      return false;
    }
    // Scheduled push: editable only until delivery time.
    if (!item.scheduled_at) {
      return false;
    }
    const scheduledAt = new Date(item.scheduled_at).getTime();
    if (Number.isNaN(scheduledAt)) {
      return false;
    }
    return scheduledAt > Date.now();
  }

  closeNotificationForm(): void {
    this.resetForms();
    this.notificationFormVisible = false;
  }

  editNotification(item: NotificationItem): void {
    if (!this.canEditNotification(item)) {
      if (Number(item.channel) !== 2 && Number(item.delivery_type) === 1) {
        this.createToast('info', '', 'Instant push notifications cannot be edited.');
      } else if (Number(item.channel) !== 2 && Number(item.delivery_type) === 2) {
        this.createToast('info', '', 'Scheduled notifications can only be edited before delivery time.');
      }
      return;
    }

    this.resetForms();
    this.notificationFormVisible = true;
    this.updateMode = true;
    this.selectedNotificationId = item.notification_id;
    this.selectedChannel = Number(item.channel) === 2 ? 2 : 1;

    if (this.isAnnouncementChannel) {
      this.announcementActive = item.is_active !== 0;
      this.announcementForm.patchValue({
        title: item.title || '',
        description: item.body || ''
      });
      this.announcementForm.markAsPristine();
      return;
    }

    this.notificationForm.patchValue({
      title: item.title,
      body: item.body,
      deliveryType: item.delivery_type === 2 ? 'scheduled' : 'instant',
      scheduledAt: this.snapScheduledAt(item.scheduled_at)
    });
    const linkType: DeepLinkType = item.link_type || 0;
    if (linkType !== 0) {
      this.getDeepLinkItems(linkType);
      if (item.link_id) {
        this.linkItems = [{ id: item.link_id, label: item.link_label || `Item #${item.link_id}` }];
      }
    } else {
      this.linkItems = [];
    }
    this.notificationForm.patchValue({
      deepLinkType: linkType,
      deepLinkId: item.link_id
    });
    this.onDeliveryTypeChange();
  }

  cloneNotification(item: NotificationItem): void {
    if (Number(item.channel) === 2) {
      this.createToast('info', '', 'Clone is available for push notifications only.');
      return;
    }
    this.resetForms();
    this.notificationFormVisible = true;
    this.updateMode = false;
    this.selectedNotificationId = null;
    this.selectedChannel = 1;
    this.notificationForm.patchValue({
      title: item.title,
      body: item.body,
      deliveryType: item.delivery_type === 2 ? 'scheduled' : 'instant',
      scheduledAt: item.delivery_type === 2 ? this.snapScheduledAt(item.scheduled_at) : null
    });
    const linkType: DeepLinkType = item.link_type || 0;
    if (linkType !== 0) {
      this.getDeepLinkItems(linkType);
      if (item.link_id) {
        this.linkItems = [{ id: item.link_id, label: item.link_label || `Item #${item.link_id}` }];
      }
    } else {
      this.linkItems = [];
    }
    this.notificationForm.patchValue({
      deepLinkType: linkType,
      deepLinkId: item.link_id
    });
    this.onDeliveryTypeChange();
  }

  resetForms(): void {
    this.notificationForm.reset({
      title: null,
      body: null,
      deliveryType: 'instant',
      scheduledAt: null,
      deepLinkType: 0,
      deepLinkId: null
    });
    this.announcementForm.reset({
      title: null,
      description: null
    });
    this.announcementActive = true;
    this.linkItems = [];
    this.updateMode = false;
    this.selectedNotificationId = null;
    this.selectedChannel = 1;
    this.onDeliveryTypeChange();
    this.onDeepLinkTypeChange(0);
  }

  resetNotificationForm(): void {
    if (this.isAnnouncementChannel) {
      this.announcementForm.reset({ title: null, description: null });
      this.announcementActive = true;
      return;
    }
    this.notificationForm.reset({
      title: null,
      body: null,
      deliveryType: 'instant',
      scheduledAt: null,
      deepLinkType: 0,
      deepLinkId: null
    });
    this.linkItems = [];
    this.onDeliveryTypeChange();
    this.onDeepLinkTypeChange(0);
  }

  submitNotification(): void {
    if (this.isAnnouncementChannel) {
      this.submitAnnouncement();
      return;
    }

    this.notificationForm.markAllAsTouched();
    if (!this.notificationForm.valid) {
      this.createToast('error', '', 'Please fill all required fields.');
      return;
    }

    const payload = {
      channel: 1,
      is_active: 1,
      title: this.notificationForm.value.title?.trim(),
      body: this.notificationForm.value.body?.trim(),
      delivery_type: this.notificationForm.value.deliveryType === 'scheduled' ? 2 : 1,
      scheduled_at: this.notificationForm.value.deliveryType === 'scheduled'
        ? this.snapScheduledAt(this.notificationForm.value.scheduledAt)
        : null,
      link_type: this.notificationForm.value.deepLinkType !== 0
        ? this.notificationForm.value.deepLinkType
        : null,
      link_id: this.notificationForm.value.deepLinkType !== 0
        ? Number(this.notificationForm.value.deepLinkId)
        : null,
      link_label: this.notificationForm.value.deepLinkType !== 0
        ? (this.linkItems.find((item) => item.id === Number(this.notificationForm.value.deepLinkId))?.label || null)
        : null
    };

    this.persistNotification(payload);
  }

  submitAnnouncement(): void {
    this.announcementForm.markAllAsTouched();
    if (!this.announcementForm.valid) {
      this.createToast('error', '', 'Please enter announcement content.');
      return;
    }

    const title = (this.announcementForm.value.title || '').trim();
    const description = this.announcementForm.value.description || '';
    const plain = String(description).replace(/<[^>]*>/g, '').trim();
    if (!title) {
      this.createToast('error', '', 'Please enter an announcement title.');
      return;
    }
    if (!plain) {
      this.createToast('error', '', 'Please enter announcement content.');
      return;
    }

    const payload = {
      channel: 2,
      is_active: this.announcementActive ? 1 : 0,
      title,
      body: description,
      delivery_type: 1,
      scheduled_at: null,
      link_type: null,
      link_id: null,
      link_label: null
    };

    this.persistNotification(payload);
  }

  private snapScheduledAt(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    date.setSeconds(0, 0);
    const snappedMinutes = Math.round(date.getMinutes() / 5) * 5;
    date.setMinutes(snappedMinutes);
    return date;
  }

  private persistNotification(payload: Record<string, unknown>): void {
    this.isLoading = true;
    const request$ = !this.updateMode
      ? this.webapi.requestJson('addNotification', payload)
      : this.webapi.requestJson('updateNotification', {
          notification_id: this.selectedNotificationId,
          ...payload
        });

    request$.subscribe(
      (data) => {
        const response = JSON.parse(data.body);
        this.notifications = (response.data || []).map((item: NotificationItem) => ({
          ...item,
          channel: Number(item.channel) === 2 ? 2 : 1,
          is_active: item.is_active === 0 ? 0 : 1
        }));
        this.isLoading = false;
        this.createToast('success', '', response.message || 'Saved successfully.');
        this.closeNotificationForm();
      },
      () => {
        this.isLoading = false;
        this.createToast('error', '', 'An error occurred. Please try again later.');
      }
    );
  }

  onStatusFilterChange(value: ListFilter): void {
    this.selectedStatusFilter = value;
    this.getNotifications();
  }

  getDeliveryTypeLabel(item: NotificationItem): string {
    if (Number(item.channel) === 2) {
      return item.is_active ? 'Active' : 'Inactive';
    }
    return item.delivery_type === 2 ? 'Scheduled' : 'Instant';
  }

  getChannelLabel(channel: number): string {
    return Number(channel) === 2 ? 'Announcement' : 'Push';
  }

  getLinkTypeLabel(value: number | null): string {
    if (value === 1) {
      return 'Resource';
    }
    if (value === 2) {
      return 'Course';
    }
    if (value === 3) {
      return 'Quiz';
    }
    return '';
  }

  getBodyPreview(item: NotificationItem): string {
    if (Number(item.channel) === 2) {
      return String(item.body || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }
    return item.body || '';
  }

  deleteNotification(notificationId: number): void {
    this.showLoad = true;
    this.webapi.requestJson('deleteNotification', { notification_id: [notificationId] }).subscribe(
      (data) => {
        const response = JSON.parse(data.body);
        this.notifications = (response.data || []).map((item: NotificationItem) => ({
          ...item,
          channel: Number(item.channel) === 2 ? 2 : 1,
          is_active: item.is_active === 0 ? 0 : 1
        }));
        this.showLoad = false;
        this.createToast('success', '', response.message || 'Deleted successfully.');
      },
      () => {
        this.showLoad = false;
        this.createToast('error', '', 'An error occurred. Please try again later.');
      }
    );
  }

  runScheduledPushProcessor(): void {
    this.isProcessingScheduled = true;
    this.webapi.requestJson('processScheduledNotifications', {}).subscribe(
      (data) => {
        this.isProcessingScheduled = false;
        let response: any = {};
        try {
          response = typeof data?.body === 'string' ? JSON.parse(data.body) : data;
        } catch (e) {
          response = data || {};
        }
        const summary = response?.data || {};
        const checked = summary.checked ?? 0;
        const sent = summary.sent ?? 0;
        const skipped = summary.skipped ?? 0;
        const errors = Array.isArray(summary.errors) ? summary.errors.length : 0;
        this.createToast(
          response?.code === 200 || data?.statusCode === 200 ? 'success' : 'error',
          '',
          response?.message
            ? `${response.message} Checked: ${checked}, sent: ${sent}, skipped: ${skipped}, errors: ${errors}.`
            : `Checked: ${checked}, sent: ${sent}, skipped: ${skipped}, errors: ${errors}.`
        );
        this.getNotifications();
      },
      () => {
        this.isProcessingScheduled = false;
        this.createToast('error', '', 'Failed to run scheduled push processor.');
      }
    );
  }

  createToast(type: string, title: string, msg: string): void {
    this.notification.create(type, title, msg);
  }
}
